import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import type { DataEnvelope, DataSource, DataSourceConfig, DataSourceExplore, DataSourceOffline, DataSourceSecrets, DataSourceSecretsPatch, DataSourceSnapshot, DataSourceTestResult, DataSourceType, Paginated } from '@/types/api'
import type { DataSourceInput } from './lib/config'

export interface DataSourceListParams {
  search?: string
  type?: DataSourceType
}

export const dataSourceKeys = {
  all: ['data-sources'] as const,
  lists: () => [...dataSourceKeys.all, 'list'] as const,
  list: (params: DataSourceListParams) => [...dataSourceKeys.lists(), params] as const,
  options: () => [...dataSourceKeys.lists(), 'options'] as const,
  details: () => [...dataSourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...dataSourceKeys.details(), id] as const,
  snapshots: () => [...dataSourceKeys.all, 'snapshot'] as const,
  snapshot: (id: string) => [...dataSourceKeys.snapshots(), id] as const,
  explores: (id: string) => [...dataSourceKeys.all, 'explore', id] as const,
  explore: (id: string, params: ExploreParams) => [...dataSourceKeys.explores(id), params] as const,
}

export interface ExploreParams {
  /** Binding path starting with the source key (default: the root). */
  path?: string
  /** Identifier field for the array at `path` (`none` = by position). */
  key_field?: string | null
  offset?: number
  /** 1–200 (default 50). */
  limit?: number
  /** 0–3 (default 1 = the node and its children). */
  depth?: number
}

/* ------------------------------------------------------------------ queries */

export function dataSourcesInfiniteOptions(params: DataSourceListParams) {
  return infiniteQueryOptions({
    queryKey: dataSourceKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<DataSource>>('/data-sources', { signal, query: { search: params.search, type: params.type, cursor: pageParam, per_page: 50 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

/** Polls while any listed source is still fetching for the first time. */
export function useDataSources(params: DataSourceListParams) {
  return useInfiniteQuery({
    ...dataSourcesInfiniteOptions(params),
    refetchInterval: (q) => (q.state.data?.pages.some((p) => p.data.some((d) => d.status === 'pending')) ? 3000 : 60_000),
  })
}

/** Every data source (up to 200) — for the editor's pickers, alias checks and previews. */
export function dataSourceOptionsQuery() {
  return queryOptions({
    queryKey: dataSourceKeys.options(),
    queryFn: async ({ signal }) => {
      const first = await api.get<Paginated<DataSource>>('/data-sources', { signal, query: { per_page: 100 } })
      if (!first.meta?.next_cursor) return first.data
      const second = await api.get<Paginated<DataSource>>('/data-sources', { signal, query: { per_page: 100, cursor: first.meta.next_cursor } })
      return [...first.data, ...second.data]
    },
    staleTime: 30_000,
  })
}

export function useDataSourceOptions(enabled = true) {
  return useQuery({ ...dataSourceOptionsQuery(), enabled })
}

export function dataSourceOptions(id: string) {
  return queryOptions({
    queryKey: dataSourceKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<DataSource>>(`/data-sources/${id}`, { signal })),
  })
}

/** Polls quickly while a fetch is pending (after create / refresh). */
export function useDataSource(id: string) {
  return useQuery({ ...dataSourceOptions(id), refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2000 : 60_000) })
}

export function snapshotOptions(id: string) {
  return queryOptions({
    queryKey: dataSourceKeys.snapshot(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<DataSourceSnapshot | null>>(`/data-sources/${id}/snapshot`, { signal })),
    staleTime: 30_000,
  })
}

export function useDataSourceSnapshot(id: string | null | undefined, enabled = true) {
  return useQuery({ ...snapshotOptions(id ?? ''), enabled: !!id && enabled })
}

/** Snapshots of several sources at once (editor live preview). */
export function useDataSourceSnapshots(ids: readonly string[]) {
  return useQueries({
    queries: ids.map((id) => ({ ...snapshotOptions(id), refetchInterval: 60_000 })),
    combine: (results) => {
      const map = new Map<string, DataSourceSnapshot | null>()
      results.forEach((r, i) => {
        if (r.data !== undefined) map.set(ids[i]!, r.data)
      })
      return map
    },
  })
}

/** `GET /data-sources/{id}/explore` — one node of the latest data (+ children page). */
export function exploreOptions(id: string, params: ExploreParams) {
  return queryOptions({
    queryKey: dataSourceKeys.explore(id, params),
    queryFn: ({ signal }) =>
      unwrap(
        api.get<DataEnvelope<DataSourceExplore>>(`/data-sources/${id}/explore`, {
          signal,
          query: { path: params.path, key_field: params.key_field ?? undefined, offset: params.offset, limit: params.limit, depth: params.depth },
        }),
      ),
    staleTime: 30_000,
  })
}

export function useDataSourceExplore(id: string | null | undefined, params: ExploreParams, enabled = true) {
  return useQuery({ ...exploreOptions(id ?? '', params), enabled: !!id && enabled })
}

/* ---------------------------------------------------------------- caching */

function patchCache(qc: QueryClient, ds: DataSource) {
  qc.setQueryData(dataSourceKeys.detail(ds.id), ds)
  qc.setQueriesData<InfiniteData<Paginated<DataSource>>>({ queryKey: dataSourceKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((d) => (d.id === ds.id ? ds : d)) })) } : data,
  )
  qc.setQueryData<DataSource[]>(dataSourceKeys.options(), (list) => (Array.isArray(list) ? list.map((d) => (d.id === ds.id ? ds : d)) : list))
}

/* ---------------------------------------------------------------- mutations */

export interface CreateDataSourceInput extends DataSourceInput {
  secrets?: DataSourceSecrets
}

export interface UpdateDataSourceInput {
  name?: string
  key?: string
  config?: DataSourceConfig
  /** Replaces all secrets (null clears) — exclusive with `secrets_patch`. */
  secrets?: DataSourceSecrets | null
  /** Changes single entries (null removes one). */
  secrets_patch?: DataSourceSecretsPatch
  refresh_s?: number
  ttl_s?: number
  /** null resets to the defaults. */
  offline?: DataSourceOffline | null
  enabled?: boolean
}

export function useCreateDataSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDataSourceInput) => unwrap(api.post<DataEnvelope<DataSource>>('/data-sources', input)),
    onSuccess: (ds) => {
      qc.setQueryData(dataSourceKeys.detail(ds.id), ds)
      void qc.invalidateQueries({ queryKey: dataSourceKeys.lists() })
    },
  })
}

export function useUpdateDataSource(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateDataSourceInput) => unwrap(api.patch<DataEnvelope<DataSource>>(`/data-sources/${id}`, input)),
    onSuccess: (ds) => {
      patchCache(qc, ds)
      void qc.invalidateQueries({ queryKey: dataSourceKeys.lists() })
      void qc.invalidateQueries({ queryKey: dataSourceKeys.explores(ds.id) })
    },
  })
}

export function useRefreshDataSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.post<DataEnvelope<DataSource>>(`/data-sources/${id}/refresh`)),
    onSuccess: (ds) => {
      patchCache(qc, ds)
      void qc.invalidateQueries({ queryKey: dataSourceKeys.snapshot(ds.id) })
      void qc.invalidateQueries({ queryKey: dataSourceKeys.explores(ds.id) })
      // The fetch runs on a queue (status may stay `ok`): look again shortly.
      for (const ms of [2500, 7000]) setTimeout(() => void qc.invalidateQueries({ queryKey: dataSourceKeys.detail(ds.id) }), ms)
    },
  })
}

export function useDeleteDataSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/data-sources/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueriesData<InfiniteData<Paginated<DataSource>>>({ queryKey: dataSourceKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((d) => d.id !== id) })) } : data,
      )
      qc.removeQueries({ queryKey: dataSourceKeys.detail(id) })
      qc.removeQueries({ queryKey: dataSourceKeys.snapshot(id) })
      void qc.invalidateQueries({ queryKey: dataSourceKeys.lists() })
    },
  })
}

export interface TestDataSourceInput {
  type: DataSourceType
  config: DataSourceConfig
  secrets?: DataSourceSecrets | null
  /** Merged over the stored secrets of `data_source_id` (exclusive with `secrets`). */
  secrets_patch?: DataSourceSecretsPatch
  data_source_id?: string
}

export function useTestDataSource() {
  return useMutation({
    mutationFn: (input: TestDataSourceInput) => unwrap(api.post<DataEnvelope<DataSourceTestResult>>('/data-sources/test', input)),
  })
}
